import { useCallback, useEffect, useState } from 'react'
import { askDayPlan, commitDayPlan, getDay, markDone, planItem } from '../api/client'
import { AreaLabel, Tags, type ProjectTitles } from '../components/ItemCard'
import { isHandled } from '../day'
import type { DayPick, Item } from '../types'

interface Props {
  projects: ProjectTitles
  onEdit: (item: Item) => void
  /** Les listes doivent refléter ce qui a été planifié ou fait ici. */
  onChanged: () => void
}

/**
 * La journée. Ce n'est pas une todo-list : Allen refuse la liste quotidienne comme
 * lieu de stockage, seul le hard landscape atterrit sur un jour. C'est une SÉLECTION
 * de next actions déjà clarifiées, et elle expire d'elle-même — une date passée
 * cesse de correspondre, la tâche redevient une next action ordinaire. Rien à
 * reporter, rien qui s'accumule.
 */
export default function Day({ projects, onEdit, onChanged }: Props) {
  const [items, setItems] = useState<Item[] | null>(null)
  const [plan, setPlan] = useState<{ date: string; message: string; picks: (DayPick & { keep: boolean })[] } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setItems((await getDay()).items)
    } catch {
      /* 401 déjà traité par le client */
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (fn: () => Promise<unknown>) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      await load()
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  // Le coach propose, on garde ou on vire au tap, PUIS ça s'écrit. Rien n'est
  // planifié sans avoir été vu.
  const compose = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const d = await askDayPlan()
      if (d.error) setError(d.error)
      else setPlan({ date: d.date, message: d.message, picks: d.picks.map((p) => ({ ...p, keep: true })) })
    } catch {
      setError('Coach injoignable.')
    } finally {
      setBusy(false)
    }
  }

  const commit = async () => {
    if (!plan || busy) return
    setBusy(true)
    try {
      await commitDayPlan(plan.date, plan.picks.filter((p) => p.keep).map((p) => p.item.id))
      setPlan(null)
      await load()
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  if (plan) {
    const kept = plan.picks.filter((p) => p.keep).length
    return (
      <>
        <div className="sec">Proposition du coach</div>
        {plan.message && <div className="bubble">{plan.message}</div>}
        {plan.picks.map((p, idx) => (
          <div className="card" key={p.item.id} style={p.keep ? undefined : { opacity: 0.4 }}>
            <div className="body">
              <div className="t">{p.item.next_action ?? p.item.title}</div>
              <div className="m">{p.why}</div>
            </div>
            <button
              className="ebtn"
              title={p.keep ? 'Retirer de la proposition' : 'Remettre'}
              onClick={() =>
                setPlan({ ...plan, picks: plan.picks.map((x, i) => (i === idx ? { ...x, keep: !x.keep } : x)) })
              }
            >
              {p.keep ? '✕' : '+'}
            </button>
          </div>
        ))}
        <div className="opts" style={{ marginTop: 14 }}>
          <button className="opt" onClick={() => void commit()} disabled={busy || kept === 0}>
            <div className="lab">
              Garder {kept} action{kept > 1 ? 's' : ''}
            </div>
          </button>
          <button className="opt other" onClick={() => setPlan(null)}>
            Annuler
          </button>
        </div>
      </>
    )
  }

  if (!items) return <div className="empty">Chargement…</div>

  const todo = items.filter((i) => !isHandled(i))
  const handled = items.filter(isHandled)

  return (
    <>
      <div className="sec">Aujourd'hui</div>
      {items.length === 0 && (
        <div className="empty">
          Rien de choisi pour aujourd'hui.
          <br />
          Demande au coach, ou pioche dans tes listes avec ☀.
        </div>
      )}
      {todo.map((i) => (
        <div className="card" key={i.id}>
          <div className="body">
            <div className="t">
              {i.next_action ?? i.title}
              <Tags item={i} /> <AreaLabel item={i} projects={projects} />
            </div>
          </div>
          <button className="ebtn" title="Retirer de la journée" onClick={() => void act(() => planItem(i.id, null))}>
            ✕
          </button>
          <button className="ebtn" onClick={() => onEdit(i)}>
            ✎
          </button>
          <button className="dbtn" onClick={() => void act(() => markDone(i.id))}>
            ✓
          </button>
        </div>
      ))}
      {handled.length > 0 && (
        <>
          <div className="sec" style={{ marginTop: 14 }}>
            Fait ({handled.length})
          </div>
          {handled.map((i) => (
            <div className="card" key={i.id} style={{ opacity: 0.5 }}>
              <div className="body">
                <div className="t">
                  ✓ {i.next_action ?? i.title}
                  {i.recurrence && <span className="tag">🔁 revient</span>}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
      {error && <div className="empty">Coach indisponible : {error}</div>}
      <div className="opts" style={{ marginTop: 16 }}>
        <button className="opt other" onClick={() => void compose()} disabled={busy}>
          {busy ? '⋯ le coach compose' : '✨ compose ma journée'}
        </button>
      </div>
    </>
  )
}
